// AGC: ContractLink component - Makes contract names clickable BaseScan links
import React from 'react'
import { ExternalLink } from 'lucide-react'
import { getBasescanUrl, getContractAddress } from '../../utils/basescan'

interface ContractLinkProps {
  /** Contract name (e.g., "FarmPro Token", "P2P Manager V2") */
  name: string
  /** Optional: Direct address (if provided, name is ignored for URL) */
  address?: string
  /** Network: 'sepolia' | 'mainnet' (default: 'sepolia') */
  network?: 'sepolia' | 'mainnet'
  /** Optional: Custom className */
  className?: string
  /** Optional: Show external link icon */
  showIcon?: boolean
}

/**
 * AGC: ContractLink - Renders contract name as clickable BaseScan link
 * 
 * @example
 * <ContractLink name="FarmPro Token" />
 * <ContractLink name="P2P Manager V2" showIcon />
 * <ContractLink name="Custom Contract" address="0x123..." />
 */
export const ContractLink: React.FC<ContractLinkProps> = ({
  name,
  address,
  network = 'sepolia',
  className = '',
  showIcon = false,
}) => {
  // AGC: Get address from name mapping or use provided address
  const contractAddress = address || getContractAddress(name)
  
  // AGC: If no address found, just render the name as plain text
  if (!contractAddress) {
    return <span className={className}>{name}</span>
  }
  
  // AGC: Generate BaseScan URL
  const basescanUrl = getBasescanUrl(contractAddress, network)
  
  return (
    <a
      href={basescanUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`text-cyan-400 hover:text-cyan-300 underline transition-colors inline-flex items-center gap-1 ${className}`}
      title={`View ${name} on BaseScan`}
    >
      {name}
      {showIcon && <ExternalLink className="w-3 h-3 inline-block" />}
    </a>
  )
}

